import { useState } from "react";
import { useSiteSettings } from "@/lib/useSiteSettings";

interface FormState {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export default function ContactPage() {
  const siteConfig = useSiteSettings();
  const [form, setForm] = useState<FormState>({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError("El mensaje no puede superar los 500 caracteres.");
      return;
    }
    setError("");
    setSubmitting(true);
    const formData = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d7np93dbmnmv4i2uk8jg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setError("Ocurrió un error al enviar el mensaje. Por favor intenta nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-wider">Estamos para ayudarte</span>
          <h1
            className="text-4xl md:text-5xl font-extrabold text-sky-900 mt-2 mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Contáctanos
          </h1>
          <p className="text-slate-500 text-lg max-w-xl mx-auto">
            ¿Tienes dudas, consultas o necesitas reportar un problema? Escríbenos y te responderemos a la brevedad.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-sky-900 mb-2">¡Mensaje enviado!</h3>
                <p className="text-slate-500 mb-6">Gracias por contactarnos. Te responderemos a la brevedad posible.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 rounded-full bg-sky-500 text-white font-semibold cursor-pointer whitespace-nowrap"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nombre completo *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Correo electrónico *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@correo.cl"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Teléfono</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+56 9 XXXX XXXX"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Asunto *</label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors bg-white"
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="Consulta general">Consulta general</option>
                      <option value="Reporte de fuga">Reporte de fuga</option>
                      <option value="Problema con medidor">Problema con medidor</option>
                      <option value="Pago de cuenta">Pago de cuenta</option>
                      <option value="Solicitud de conexión">Solicitud de conexión</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Mensaje * <span className="text-slate-400 font-normal">({form.message.length}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Describe tu consulta o problema..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors resize-none"
                  />
                </div>
                {error && (
                  <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>{error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-base transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Enviar mensaje</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="bg-sky-50 rounded-2xl p-6 border border-sky-100">
              <h3 className="font-bold text-sky-900 text-lg mb-5">Información de contacto</h3>
              <div className="space-y-4">
                {[
                  { icon: "ri-phone-line", label: "Teléfono", value: siteConfig.phone, href: `tel:${siteConfig.phone}` },
                  { icon: "ri-mail-line", label: "Correo", value: siteConfig.email, href: `mailto:${siteConfig.email}` },
                  { icon: "ri-map-pin-line", label: "Dirección", value: siteConfig.address, href: undefined },
                  { icon: "ri-time-line", label: "Horario", value: siteConfig.schedule, href: undefined },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-sky-100 text-sky-600 flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">{item.label}</span>
                      {item.href ? (
                        <a href={item.href} className="text-sm font-semibold text-sky-900 hover:text-sky-600 cursor-pointer">
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-sm font-semibold text-sky-900">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* WhatsApp */}
            <a
              href={`https://wa.me/${siteConfig.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-base transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              Contactar por WhatsApp
            </a>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-slate-100 h-52">
              <iframe
                title="Ubicación APR"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3329.0!2d-71.2!3d-35.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDI0JzAwLjAiUyA3McKwMTInMDAuMCJX!5e0!3m2!1ses!2scl!4v1680000000000!5m2!1ses!2scl"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
